import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { IMAGES } from "../content";
import { EASE } from "../motion";
import { scrollToId } from "./Nav";

const LINES = ["A more human way", "to find support,", "understanding and growth."];

export default function Hero({ onOverview }) {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const [scene, setScene] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "38%"]);

  useEffect(() => {
    if (reduced) return;
    const t = setInterval(() => setScene((s) => (s + 1) % 2), 9000);
    return () => clearInterval(t);
  }, [reduced]);

  return (
    <section id="vision" data-testid="section-vision" ref={ref} className="relative min-h-[100svh] overflow-hidden bg-[#26342E]">
      <motion.div className="absolute inset-0" style={{ y: imgY }}>
        <img
          src={IMAGES.hero}
          alt="A serene, light-filled interior with natural textures, the calm the proposed brand begins with"
          className="absolute inset-0 w-full h-[115%] object-cover animate-drift"
        />
        <motion.img
          src={IMAGES.therapist}
          alt=""
          initial={false}
          animate={{ opacity: scene === 1 ? 1 : 0 }}
          transition={{ duration: 2.4, ease: "easeInOut" }}
          className="absolute inset-0 w-full h-[115%] object-cover object-[center_22%] animate-drift"
        />
        <div className="absolute inset-0 bg-[#26342E]/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#26342E] via-transparent to-[#26342E]/35" />
      </motion.div>

      <motion.div
        className="relative z-10 min-h-[100svh] flex flex-col justify-end max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 pb-28 sm:pb-32 pt-32"
        style={{ opacity: fade, y: textY }}
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.25 }}
          className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.3em] text-[#F6F0E6]/75 mb-8"
          data-testid="hero-eyebrow"
        >
          A brand and digital experience proposal by Blank Space Media
        </motion.p>

        <h1 className="font-serif font-light text-[#F6F0E6] text-4xl sm:text-5xl lg:text-[4.4rem] leading-[1.06] tracking-tight max-w-4xl" data-testid="hero-headline">
          {LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
              <motion.span
                className="block"
                initial={{ y: "112%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.15, ease: EASE, delay: 0.45 + i * 0.16 }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 1.15 }}
          className="text-[#F6F0E6]/75 text-base sm:text-lg leading-relaxed max-w-xl mt-8"
          data-testid="hero-subcopy"
        >
          A proposed brand and digital experience connecting individuals, families, young people, schools and future
          practitioners with the support and knowledge they need.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 1.35 }}
          className="flex flex-wrap items-center gap-4 mt-10"
        >
          <button
            data-testid="hero-cta-vision"
            onClick={() => scrollToId("experience")}
            className="bg-[#F6F0E6] text-[#26342E] text-xs font-bold uppercase tracking-[0.18em] px-8 py-4 rounded-full transition-[background-color,transform] duration-300 hover:bg-white hover:-translate-y-0.5 cursor-pointer"
          >
            Experience the Vision
          </button>
          <button
            data-testid="hero-cta-proposal"
            onClick={() => scrollToId("investment")}
            className="text-[#F6F0E6] text-xs font-bold uppercase tracking-[0.18em] px-8 py-4 rounded-full border border-[#F6F0E6]/35 transition-[background-color,border-color,transform] duration-300 hover:bg-[#F6F0E6]/10 hover:border-[#F6F0E6]/60 hover:-translate-y-0.5 cursor-pointer"
          >
            View the Proposal
          </button>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, ease: EASE, delay: 1.6 }}
        >
          <button
            data-testid="hero-overview-link"
            onClick={onOverview}
            className="mt-8 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#F6F0E6]/55 underline underline-offset-8 decoration-[#F6F0E6]/25 transition-colors duration-300 hover:text-[#F6F0E6] cursor-pointer"
          >
            Executive overview · two minutes
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3"
        aria-hidden="true"
      >
        <span className="text-[9px] uppercase tracking-[0.35em] text-[#F6F0E6]/50">Scroll</span>
        <span className="block w-px h-10 bg-[#F6F0E6]/40 animate-scrollcue" />
      </motion.div>
    </section>
  );
}
